// notifySound.js — the notification bell's voice.
//
// One place decides what a notification SOUNDS like: the chime, the spoken
// sentence, its language. The settings belong to the COMPANY (RestSetting row
// "SoundSettings", edited under Setup → Business Settings → Sound Settings),
// so every till and every phone signed in to the property hears the same
// thing. The only per-device choice is the speaker switch beside the bell.
//
// Speech is the browser's own (speechSynthesis): no audio files, no server,
// and English works on a till with no internet. Which voices exist depends on
// the device - Chrome on a PC ships Hindi and English, other languages vary -
// so the settings screen shows what THIS device can say.
//
// Browsers refuse to play audio until the user has interacted with the page,
// so the audio context is created on the first click anywhere and the first
// order after a cold page load may be silent. The badge and the row still
// appear; there is no way around that rule.
import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../../config';

export const SOUND_SETTINGS_KEY = 'SoundSettings';
export const SOUND_SETTINGS_EVENT = 'soundSettingsChanged';
const MUTE_STORAGE_KEY = 'notifSoundMuted';

// Ready-made sentences. {no} becomes the table name or the room number,
// {order} the order number. The admin can rewrite any of them; these only
// fill the box when a language is picked.
export const LANGUAGE_PRESETS = [
  {
    code: 'en-IN',
    label: 'English',
    native: 'English',
    table: 'Order received from table number {no}',
    room: 'Order received from room number {no}',
    invoice: 'Invoice requested for order number {order} ({no})',
    appTable: 'Order received on {no} from app',
    appRoom: 'Order received on room {no} from app',
    swiggy: 'New Swiggy order received, number {order}',
    zomato: 'New Zomato order received, number {order}',
  },
  {
    code: 'hi-IN',
    label: 'Hindi',
    native: 'हिन्दी',
    table: 'टेबल नंबर {no} से ऑर्डर आया है',
    room: 'रूम नंबर {no} से ऑर्डर आया है',
    invoice: 'ऑर्डर नंबर {order} ({no}) का बिल मांगा गया है',
    appTable: 'ऐप से {no} पर ऑर्डर आया है',
    appRoom: 'ऐप से रूम नंबर {no} पर ऑर्डर आया है',
    swiggy: 'स्विगी से नया ऑर्डर आया है, नंबर {order}',
    zomato: 'ज़ोमैटो से नया ऑर्डर आया है, नंबर {order}',
  },
  {
    code: 'mr-IN',
    label: 'Marathi',
    native: 'मराठी',
    table: 'टेबल नंबर {no} वरून ऑर्डर आली आहे',
    room: 'रूम नंबर {no} वरून ऑर्डर आली आहे',
    invoice: 'ऑर्डर क्रमांक {order} ({no}) साठी बिल मागितले आहे',
    appTable: 'ॲपवरून {no} वर ऑर्डर आली आहे',
    appRoom: 'ॲपवरून रूम नंबर {no} वर ऑर्डर आली आहे',
    swiggy: 'स्विगीवरून नवीन ऑर्डर आली आहे, क्रमांक {order}',
    zomato: 'झोमॅटोवरून नवीन ऑर्डर आली आहे, क्रमांक {order}',
  },
  {
    code: 'gu-IN',
    label: 'Gujarati',
    native: 'ગુજરાતી',
    table: 'ટેબલ નંબર {no} થી ઓર્ડર આવ્યો છે',
    room: 'રૂમ નંબર {no} થી ઓર્ડર આવ્યો છે',
    invoice: 'ઓર્ડર નંબર {order} ({no}) માટે બિલ માંગ્યું છે',
    appTable: 'એપથી {no} પર ઓર્ડર આવ્યો છે',
    appRoom: 'એપથી રૂમ નંબર {no} પર ઓર્ડર આવ્યો છે',
    swiggy: 'સ્વિગીથી નવો ઓર્ડર આવ્યો છે, નંબર {order}',
    zomato: 'ઝોમેટોથી નવો ઓર્ડર આવ્યો છે, નંબર {order}',
  },
  {
    code: 'pa-IN',
    label: 'Punjabi',
    native: 'ਪੰਜਾਬੀ',
    table: 'ਟੇਬਲ ਨੰਬਰ {no} ਤੋਂ ਆਰਡਰ ਆਇਆ ਹੈ',
    room: 'ਰੂਮ ਨੰਬਰ {no} ਤੋਂ ਆਰਡਰ ਆਇਆ ਹੈ',
    invoice: 'ਆਰਡਰ ਨੰਬਰ {order} ({no}) ਲਈ ਬਿੱਲ ਮੰਗਿਆ ਗਿਆ ਹੈ',
    appTable: 'ਐਪ ਤੋਂ {no} ਤੇ ਆਰਡਰ ਆਇਆ ਹੈ',
    appRoom: 'ਐਪ ਤੋਂ ਰੂਮ ਨੰਬਰ {no} ਤੇ ਆਰਡਰ ਆਇਆ ਹੈ',
    swiggy: 'ਸਵਿਗੀ ਤੋਂ ਨਵਾਂ ਆਰਡਰ ਆਇਆ ਹੈ, ਨੰਬਰ {order}',
    zomato: 'ਜ਼ੋਮੈਟੋ ਤੋਂ ਨਵਾਂ ਆਰਡਰ ਆਇਆ ਹੈ, ਨੰਬਰ {order}',
  },
  {
    code: 'bn-IN',
    label: 'Bengali',
    native: 'বাংলা',
    table: 'টেবিল নম্বর {no} থেকে অর্ডার এসেছে',
    room: 'রুম নম্বর {no} থেকে অর্ডার এসেছে',
    invoice: 'অর্ডার নম্বর {order} ({no}) এর জন্য বিল চাওয়া হয়েছে',
    appTable: 'অ্যাপ থেকে {no} এ অর্ডার এসেছে',
    appRoom: 'অ্যাপ থেকে রুম নম্বর {no} এ অর্ডার এসেছে',
    swiggy: 'সুইগি থেকে নতুন অর্ডার এসেছে, নম্বর {order}',
    zomato: 'জোমাটো থেকে নতুন অর্ডার এসেছে, নম্বর {order}',
  },
  {
    code: 'ta-IN',
    label: 'Tamil',
    native: 'தமிழ்',
    table: 'மேசை எண் {no} இலிருந்து ஆர்டர் வந்துள்ளது',
    room: 'அறை எண் {no} இலிருந்து ஆர்டர் வந்துள்ளது',
    invoice: 'ஆர்டர் எண் {order} ({no}) க்கு பில் கேட்கப்பட்டுள்ளது',
    appTable: 'ஆப் மூலம் {no} க்கு ஆர்டர் வந்துள்ளது',
    appRoom: 'ஆப் மூலம் அறை எண் {no} க்கு ஆர்டர் வந்துள்ளது',
    swiggy: 'ஸ்விக்கியிலிருந்து புதிய ஆர்டர் வந்துள்ளது, எண் {order}',
    zomato: 'சொமாட்டோவிலிருந்து புதிய ஆர்டர் வந்துள்ளது, எண் {order}',
  },
  {
    code: 'te-IN',
    label: 'Telugu',
    native: 'తెలుగు',
    table: 'టేబుల్ నంబర్ {no} నుండి ఆర్డర్ వచ్చింది',
    room: 'రూమ్ నంబర్ {no} నుండి ఆర్డర్ వచ్చింది',
    invoice: 'ఆర్డర్ నంబర్ {order} ({no}) కోసం బిల్లు అడిగారు',
    appTable: 'యాప్ నుండి {no} పై ఆర్డర్ వచ్చింది',
    appRoom: 'యాప్ నుండి రూమ్ నంబర్ {no} పై ఆర్డర్ వచ్చింది',
    swiggy: 'స్విగ్గీ నుండి కొత్త ఆర్డర్ వచ్చింది, నంబర్ {order}',
    zomato: 'జొమాటో నుండి కొత్త ఆర్డర్ వచ్చింది, నంబర్ {order}',
  },
];

// The five rings, like a phone's ringtone list. Each is a list of notes
// [frequency Hz, start s, length s], played by playChime. Built in the
// browser - no audio files to ship or get blocked.
const trill = (bursts, tones, step, len) => {
  const notes = [];
  for (let b = 0; b < bursts; b += 1) {
    for (let i = 0; i < tones; i += 1) {
      notes.push([i % 2 ? 1250 : 1000, b * 0.75 + i * step, len]);
    }
  }
  return notes;
};
export const CHIME_PRESETS = [
  {
    key: 'doorbell',
    label: 'Doorbell (two tones)',
    wave: 'sine',
    notes: [[880, 0, 0.28], [1320, 0.16, 0.28]],
  },
  {
    key: 'triple',
    label: 'Triple beep',
    wave: 'sine',
    notes: [[1047, 0, 0.12], [1047, 0.18, 0.12], [1047, 0.36, 0.12]],
  },
  {
    key: 'rising',
    label: 'Rising chime',
    wave: 'sine',
    notes: [[659, 0, 0.18], [784, 0.16, 0.18], [988, 0.32, 0.18], [1319, 0.48, 0.4]],
  },
  {
    key: 'ringring',
    label: 'Ring ring (phone)',
    wave: 'sine',
    notes: trill(2, 10, 0.04, 0.045),
  },
  {
    key: 'alarm',
    label: 'Alarm (four pulses)',
    wave: 'triangle',
    notes: [[1568, 0, 0.15], [1568, 0.25, 0.15], [1568, 0.5, 0.15], [1568, 0.75, 0.15]],
  },
];
export const DEFAULT_CHIME = CHIME_PRESETS[0].key;
export const chimePreset = (key) =>
  CHIME_PRESETS.find((c) => c.key === key) || CHIME_PRESETS[0];
// How long a ring lasts, so the sentence starts after it, not over it.
const chimeLength = (key) => {
  const p = chimePreset(key);
  return Math.max(...p.notes.map(([, at, len]) => at + len));
};

// The value the Language box shows when the admin typed their own code.
export const OTHER_LANGUAGE = 'other';

export const presetFor = (code) =>
  LANGUAGE_PRESETS.find((p) => p.code === code) || null;

// Every notification the bell can voice. The settings screen and the
// announcer both walk this list, so adding a notification is one row here
// plus one call to announce() where it arrives.
//
//   texts  - the editable sentences: which setting key holds each, its label,
//            its help line, and which LANGUAGE_PRESETS field fills it.
//   tests  - the Test buttons: label + the data announce() would get.
export const NOTIFICATION_KINDS = [
  {
    key: 'qrOrder',
    title: 'QR order received',
    description:
      'A guest placed an order by scanning a table or room QR. Spoken with the table name or the room number.',
    texts: [
      {
        key: 'textTable',
        preset: 'table',
        label: 'When the order is from a table',
        help: '{no} becomes the table name, e.g. Garden-01',
      },
      {
        key: 'textRoom',
        preset: 'room',
        label: 'When the order is from a room',
        help: '{no} becomes the room number, e.g. 302',
      },
    ],
    tests: [
      { label: 'Test table', data: { isRoom: false, no: 'Garden-01' } },
      { label: 'Test room', data: { isRoom: true, no: '302' } },
    ],
  },
  {
    key: 'appOrder',
    title: 'App order received',
    description:
      'A waiter placed an order from the restaurant app. Spoken with the table name or the room number. The Dine In table grid marks the table by itself.',
    texts: [
      {
        key: 'textTable',
        preset: 'appTable',
        label: 'When the order is on a table',
        help: '{no} becomes the table name, e.g. Garden-05',
      },
      {
        key: 'textRoom',
        preset: 'appRoom',
        label: 'When the order is on a room',
        help: '{no} becomes the room number, e.g. 302',
      },
    ],
    tests: [
      { label: 'Test table', data: { isRoom: false, no: 'Garden-05' } },
      { label: 'Test room', data: { isRoom: true, no: '302' } },
    ],
  },
  {
    key: 'invoiceRequest',
    title: 'Invoice requested',
    description:
      'A waiter asked for the bill from the restaurant app. Spoken with the order number and the table.',
    texts: [
      {
        key: 'text',
        preset: 'invoice',
        label: 'When an invoice is requested',
        help: '{order} becomes the order number, {no} the table, e.g. 3243 and Garden-04',
      },
    ],
    tests: [{ label: 'Test', data: { order: 3243, no: 'Garden-04' } }],
  },
  {
    key: 'swiggyOrder',
    title: 'Swiggy order received',
    description:
      'A new Swiggy order arrived (through WeraFoods). Heard only by users who can open Restaurant Orders → Swiggy / Zomato.',
    texts: [
      {
        key: 'text',
        preset: 'swiggy',
        label: 'When a Swiggy order arrives',
        help: '{order} becomes the last 4 digits of the Swiggy order number, e.g. 4396',
      },
    ],
    tests: [{ label: 'Test', data: { order: '4396' } }],
  },
  {
    key: 'zomatoOrder',
    title: 'Zomato order received',
    description:
      'A new Zomato order arrived (through WeraFoods). Heard only by users who can open Restaurant Orders → Swiggy / Zomato.',
    texts: [
      {
        key: 'text',
        preset: 'zomato',
        label: 'When a Zomato order arrives',
        help: '{order} becomes the last 4 digits of the Zomato order number, e.g. 4727',
      },
    ],
    tests: [{ label: 'Test', data: { order: '4727' } }],
  },
];

export const DEFAULT_SOUND_SETTINGS = {
  qrOrder: {
    enabled: true,
    chime: true,
    chimeStyle: DEFAULT_CHIME,
    speak: true,
    language: 'en-IN',
    textTable: LANGUAGE_PRESETS[0].table,
    textRoom: LANGUAGE_PRESETS[0].room,
    volume: 1,
  },
  appOrder: {
    enabled: true,
    chime: true,
    chimeStyle: DEFAULT_CHIME,
    speak: true,
    language: 'en-IN',
    textTable: LANGUAGE_PRESETS[0].appTable,
    textRoom: LANGUAGE_PRESETS[0].appRoom,
    volume: 1,
  },
  invoiceRequest: {
    enabled: true,
    chime: true,
    chimeStyle: DEFAULT_CHIME,
    speak: true,
    language: 'en-IN',
    text: LANGUAGE_PRESETS[0].invoice,
    volume: 1,
  },
  // Rings of their own, so the kitchen can tell a Swiggy / Zomato order
  // from a table's by ear.
  swiggyOrder: {
    enabled: true,
    chime: true,
    chimeStyle: 'ringring',
    speak: true,
    language: 'en-IN',
    text: LANGUAGE_PRESETS[0].swiggy,
    volume: 1,
  },
  zomatoOrder: {
    enabled: true,
    chime: true,
    chimeStyle: 'rising',
    speak: true,
    language: 'en-IN',
    text: LANGUAGE_PRESETS[0].zomato,
    volume: 1,
  },
};

// Saved JSON over the defaults, per notification, so a setting added later
// has a value on a company that saved before it existed.
export function mergeSoundSettings(json) {
  let saved = {};
  if (json) {
    try {
      saved = typeof json === 'string' ? JSON.parse(json) : json;
    } catch {
      saved = {};
    }
  }
  const out = {};
  Object.keys(DEFAULT_SOUND_SETTINGS).forEach((k) => {
    out[k] = { ...DEFAULT_SOUND_SETTINGS[k], ...((saved && saved[k]) || {}) };
  });
  return out;
}

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`,
});

export async function fetchSoundSettings(companyId) {
  const res = await fetch(
    `${config.API_BASE_URL}/businesssettings/${companyId}/${SOUND_SETTINGS_KEY}`,
    { headers: authHeaders() },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const d = await res.json();
  return mergeSoundSettings(d?.json);
}

export async function saveSoundSettings(companyId, settings) {
  const res = await fetch(`${config.API_BASE_URL}/businesssettings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      CompanyId: companyId,
      Key: SOUND_SETTINGS_KEY,
      Json: JSON.stringify(settings),
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  // The bell is mounted on every screen and keeps its own copy; tell it.
  try {
    window.dispatchEvent(
      new CustomEvent(SOUND_SETTINGS_EVENT, { detail: settings }),
    );
  } catch {
    /* nothing listening */
  }
}

// ── Device-level mute ─────────────────────────────────────────────────────
export const isMuted = () => {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};
const storeMuted = (m) => {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, m ? '1' : '0');
  } catch {
    /* private mode */
  }
};

// ── Audio primitives ──────────────────────────────────────────────────────
let audioCtx = null;

// Create (or resume) the audio context. Must run from a user gesture the
// first time; safe to call any time after.
export function unlockAudio() {
  try {
    if (audioCtx) {
      if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
      return;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioCtx = new Ctx();
  } catch {
    /* no audio on this device */
  }
}

let unlockInstalled = false;
function installUnlock() {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const once = () => unlockAudio();
  window.addEventListener('click', once, { once: true });
  window.addEventListener('keydown', once, { once: true });
  window.addEventListener('touchstart', once, { once: true });
}

// The ring, built in the browser from one of CHIME_PRESETS. No audio file: a
// bundled mp3 is one more asset to ship, cache-bust and get blocked. The
// default is two short notes a fifth apart - a doorbell rather than an error
// beep, which is what a kitchen wants to hear twenty times a night.
export function playChime(volume = 1, style = DEFAULT_CHIME) {
  const ctx = audioCtx;
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const preset = chimePreset(style);
    const peak = 0.25 * Math.max(0.05, Math.min(1, Number(volume) || 1));
    preset.notes.forEach(([freq, at, len]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = preset.wave || 'sine';
      osc.frequency.value = freq;
      const t0 = ctx.currentTime + at;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + len);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + len + 0.02);
    });
    return true;
  } catch {
    return false;
  }
}

// Voices this device has for a language: exact tag first (hi-IN), then the
// base language (hi-*). Empty means the browser will fall back to its default
// voice, which may not pronounce the script at all.
export function voicesFor(lang) {
  try {
    const all = window.speechSynthesis?.getVoices?.() || [];
    if (!lang) return all;
    const l = String(lang).toLowerCase();
    const base = l.split('-')[0];
    const exact = all.filter((v) => (v.lang || '').toLowerCase() === l);
    if (exact.length) return exact;
    return all.filter(
      (v) => (v.lang || '').toLowerCase().split('-')[0] === base,
    );
  } catch {
    return [];
  }
}

export const speechSupported = () =>
  typeof window !== 'undefined' && !!window.speechSynthesis;

export function speak(text, { lang = 'en-IN', volume = 1 } = {}) {
  if (!text || !speechSupported()) return false;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang || 'en-IN';
    u.volume = Math.max(0, Math.min(1, Number(volume) || 1));
    u.rate = 0.95;
    const voice = voicesFor(u.lang)[0];
    if (voice) u.voice = voice;
    window.speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
}

// The sentence for one notification. A kind with a single sentence keeps it
// in `text`; the QR order has one for tables and one for rooms.
// {no} = table / room, {order} = order number. A sentence that names the
// table in brackets loses the brackets when there is no table (a room order
// asking for its bill), rather than saying "order 3243, open bracket".
export const buildText = (cfg, { isRoom, no, order } = {}) => {
  let tpl = cfg.text ?? (isRoom ? cfg.textRoom || cfg.textTable : cfg.textTable);
  tpl = String(tpl || '');
  if (no == null || String(no).trim() === '') {
    tpl = tpl.replace(/\s*\(\s*\{no\}\s*\)/gi, '');
  }
  return tpl
    .replace(/\{no\}/gi, String(no ?? ''))
    .replace(/\{order\}/gi, String(order ?? ''))
    .trim();
};

// Play one notification with the given settings: chime, then the sentence.
// `data` = { isRoom, no, order }. Honours the device mute unless `force`.
export function announceWith(settings, kind, data = {}, { force = false } = {}) {
  if (!force && isMuted()) return false;
  const cfg = settings?.[kind];
  if (!cfg || cfg.enabled === false) return false;
  const volume = cfg.volume ?? 1;
  let delay = 0;
  let played = false;
  if (cfg.chime !== false) {
    played = playChime(volume, cfg.chimeStyle) || played;
    delay = Math.round(chimeLength(cfg.chimeStyle) * 1000) + 150;
  }
  if (cfg.speak !== false) {
    const text = buildText(cfg, data);
    if (text) {
      played = true;
      setTimeout(() => speak(text, { lang: cfg.language, volume }), delay);
    }
  }
  return played;
}

// The bell's hook: keeps the company's settings fresh (loaded once, replaced
// when Sound Settings saves) and exposes announce(kind, data) plus the device
// mute switch.
export function useNotificationSound(companyId) {
  const [settings, setSettings] = useState(DEFAULT_SOUND_SETTINGS);
  const [muted, setMutedState] = useState(isMuted);
  const settingsRef = useRef(settings);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    installUnlock();
    // Chrome fills the voice list asynchronously; asking once now means it is
    // ready by the time the first order arrives.
    try {
      window.speechSynthesis?.getVoices?.();
    } catch {
      /* no speech on this device */
    }
  }, []);

  useEffect(() => {
    if (!companyId) return undefined;
    let alive = true;
    fetchSoundSettings(companyId)
      .then((s) => {
        if (alive) setSettings(s);
      })
      .catch(() => {
        /* defaults stay */
      });
    const onChange = (e) => {
      if (e?.detail) setSettings(mergeSoundSettings(e.detail));
    };
    window.addEventListener(SOUND_SETTINGS_EVENT, onChange);
    return () => {
      alive = false;
      window.removeEventListener(SOUND_SETTINGS_EVENT, onChange);
    };
  }, [companyId]);

  const announce = useCallback(
    (kind, data) => announceWith(settingsRef.current, kind, data),
    [],
  );

  const setMuted = useCallback((m) => {
    storeMuted(m);
    setMutedState(!!m);
  }, []);

  return { settings, announce, muted, setMuted };
}
